import * as React from 'react';

import { Button } from '@mui/material';
import { requestEmailCode } from '@realadvisor/auth-client';
import { Flex } from 'react-system';

import { useLocale } from '@legacy/hooks/locale';
import { useTheme } from '@legacy/hooks/theme';
import { describeAuthError } from '@legacy/routes/Login/authErrors';

// Matches the SMS resend so the two verify screens behave the same. There is no
// server-side throttle on request-auth-code, so this cooldown is the only thing
// pacing a user who keeps pressing the button; a code stays valid for 5 minutes,
// so asking for a new one sooner than this is almost never what they need.
const RESEND_COOLDOWN_SECONDS = 60;

type Props = {
  email: string;
  tenantId: string | null;
};

export const ResendEmailCodeButton = ({ email, tenantId }: Props) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const [secondsLeft, setSecondsLeft] = React.useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (secondsLeft <= 0) {
      return;
    }
    const timer = setInterval(() => {
      setSecondsLeft(current => (current <= 1 ? 0 : current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const resend = async () => {
    setResending(true);
    setError(null);
    setSent(false);
    try {
      const response = await requestEmailCode({ email, tenantId });
      if (response.ok) {
        setSent(true);
        setSecondsLeft(RESEND_COOLDOWN_SECONDS);
      } else {
        const detail = await response
          .json()
          .then(body => body.error)
          .catch(() => null);
        setError(describeAuthError(detail, t));
      }
    } catch (err) {
      setError(describeAuthError(err instanceof Error ? err.message : null, t));
    } finally {
      setResending(false);
    }
  };

  return (
    <Flex flexDirection="column" alignItems="center" css={{ gap: 4 }}>
      <Button
        variant="text"
        size="small"
        loading={resending}
        disabled={secondsLeft > 0}
        onClick={resend}
      >
        {secondsLeft > 0
          ? t('{{seconds}}s before retrying', {
              seconds: secondsLeft.toString(),
            })
          : t('Resend code')}
      </Button>
      {sent && (
        <span css={{ fontSize: 12, color: colors.successText }}>
          {t('A new code is on its way.')}
        </span>
      )}
      {error != null && (
        <span css={{ fontSize: 12, color: colors.errorText }}>{error}</span>
      )}
    </Flex>
  );
};
